import React, { useState, useRef } from "react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker as MuiDatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { FormHelperText, Typography, FormControl, Grid } from "@mui/material";
import { styled } from "@mui/material/styles";
import calenderIcon from "../../assets/images/calenderIcon.svg";
import CustomToolbar from "./customToolBar";
import { getDateLocale } from "../../utils/date";

const StyledFormControl = styled(FormControl)({
  minWidth: "100%",
});

const StyledLabel = styled(Typography)({
  lineHeight: "30px",
});

const StyledFormHelperText = styled(FormHelperText)(({ theme }) => ({
  margin: theme?.spacing(0.5, 0.5, 0, 0.5),
}));

export default function InlineDatePicker(props) {
  const {
    name,
    label,
    format = "dd/MM/yyyy",
    onChange,
    variant = "outlined",
    required,
    value,
    labelClass,
    error = null,
    disableFuture = false,
    disabled = false,
    maxDate,
    minDate,
    onDateSelect,
    DVA = false,
    ...others
  } = props;

  const [open, setOpen] = useState(false);
  const isInteractingRef = useRef(false);
  const preventReopenRef = useRef(false);
  const iconButtonRef = useRef(null);

  const parseValue = (val) => {
    if (!val) return null;
    if (val instanceof Date) return val;
    if (typeof val === "string") {
      const parsedDate = new Date(val);
      return isNaN(parsedDate.getTime()) ? null : parsedDate;
    }
    return null;
  };

  const parsedValue = parseValue(value);

  const handleInputClick = () => {
    if (iconButtonRef.current && !disabled) {
      if (DVA) {
        preventReopenRef.current = false;
      }
      iconButtonRef.current.click();
      iconButtonRef.current.focus();
    }
  };

  const getInputStyles = () => {
    let borderColor = "#B72755";
    let borderWidth = "1px";
    let textColor = "inherit";
    let fontSize = "inherit";
    let fontWeight = "normal";
    let borderRadius = 0;

    if (DVA) {
      borderWidth = "2px";
      borderColor = parsedValue ? "#0D0A15" : "#6E6C73";
      textColor = "#0D0A15";
      fontSize = "16px";
      fontWeight = 500;
      borderRadius = "8px";
    }

    return {
      backgroundColor: "#fff",
      "& .MuiOutlinedInput-root": {
        borderRadius,
        "& .MuiOutlinedInput-input": {
          color: textColor,
          fontSize,
          fontWeight,
        },
        "& .MuiOutlinedInput-input::placeholder": {
          textAlign: "left",
          direction: "ltr",
        },
      },
      "& .MuiInputBase-inputSizeSmall": {
        padding: "8px 12px",
      },
      "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
        border: `${borderWidth} solid ${borderColor}`,
      },
      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
        border: `${borderWidth} solid ${borderColor}`,
      },
      ...(DVA && {
        "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
          border: `${borderWidth} solid ${borderColor}`,
        },
      }),
    };
  };

  return (
    <StyledFormControl
      variant={variant}
      {...(error && { error: "true", helpertext: error })}
    >
      <Grid container spacing={0}>
        {!DVA && (
          <Grid item xs={12} md={4}>
            <StyledLabel
              component="p"
              className={`${required ? "inputLabel required" : "inputLabel"} ${
                labelClass || ""
              }`}
            >
              {label}
            </StyledLabel>
          </Grid>
        )}
        <Grid item xs={12} md={DVA ? 12 : 8}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getDateLocale()}>
            <MuiDatePicker
              format={format}
              value={parsedValue}
              name={name}
              onChange={(date) => {
                onChange(name, date);
              }}
              onAccept={(date) => {
                onChange(name, date);
                setOpen(false);
                if (DVA) {
                  preventReopenRef.current = true;
                  isInteractingRef.current = true;
                }
                onDateSelect?.();
              }}
              views={["year", "month", "day"]}
              disableFuture={disableFuture}
              disabled={disabled}
              open={disabled ? false : open}
              maxDate={maxDate}
              minDate={minDate}
              onOpen={() => {
                if (DVA) {
                  isInteractingRef.current = true;
                  preventReopenRef.current = false;
                }
                setOpen(true);
              }}
              onClose={() => {
                setOpen(false);
                if (DVA) {
                  isInteractingRef.current = false;
                }
              }}
              {...(DVA && { closeOnSelect: true })}
              slotProps={{
                textField: {
                  variant: "outlined",
                  size: "small",
                  fullWidth: true,
                  error: !!error,
                  onClick: handleInputClick,
                  ...(DVA && {
                    onFocus: () => {
                      if (!isInteractingRef?.current && !preventReopenRef?.current) {
                        setOpen(true);
                        isInteractingRef.current = true;
                      }
                    },
                    onBlur: () => {
                      setTimeout(() => {
                        if (!open) {
                          preventReopenRef.current = false;
                          isInteractingRef.current = false;
                        }
                      }, 100);
                    },
                  }),
                  onKeyDown: (event) => event.preventDefault(),
                  sx: getInputStyles(),
                  ...others,
                },
                openPickerButton: {
                  ref: iconButtonRef,
                  "aria-label": "change date",
                  className: DVA ? "dva-icon-button" : "",
                  disableRipple: !!DVA,
                  sx: DVA
                    ? {
                        "&:hover": {
                          backgroundColor: "transparent !important",
                          boxShadow: "none",
                        },
                        "&:focus": {
                          backgroundColor: "transparent !important",
                          boxShadow: "none",
                        },
                      }
                    : {},
                },
              }}
              slots={{
                toolbar: (toolbarProps) => <CustomToolbar {...toolbarProps} />,
                ...(DVA && {
                  openPickerIcon: () => (
                    <img
                      src={calenderIcon}
                      alt="calender-icon"
                      className="dva-icon"
                    />
                  ),
                }),
              }}
              showToolbar="true"
            />
          </LocalizationProvider>
          <StyledFormHelperText>{error !== "" && error}</StyledFormHelperText>
        </Grid>
      </Grid>
    </StyledFormControl>
  );
}